/**
 * InfoSidePanel.tsx — Fixed right-edge slide-out panel for full tract metrics.
 *
 * Slides in from the right over the map canvas when open, keeping the map fully
 * interactive behind it. The panel owns the single scroll surface (overflow-y-auto
 * on the body); ScoreDrillDown renders with no scroll of its own.
 *
 * Layout (desktop, ≥md):
 *   fixed top-12 right-0 bottom-0 w-[420px]  ← clears h-12 TopBar
 *   ├── header  h-12 flex-shrink-0            ← close button + tract header
 *   └── body    flex-1 overflow-y-auto        ← ScoreDrillDown content
 *
 * Accessibility: Escape key closes; focus returns to the trigger button on close.
 */
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Spinner } from './ui/spinner'
import { ScoreDrillDown, scoreColor } from './ScoreDrillDown'
import type { LocationClassification } from '@/lib/locationClassification'
import { SCORE_LABELS, dominantTypeLabel } from '@/lib/tractModel'
import type { CensusTract, SiteScoreResult } from '@/lib/tractModel'

export interface InfoSidePanelProps {
  open: boolean
  onClose: () => void
  tract: CensusTract | null
  siteScore?: SiteScoreResult | null
  siteScoreLoading?: boolean
  siteScoreError?: string | null
  /** T-512: searched site lat/lng, forwarded to ScoreDrillDown's block classification readout. */
  site?: { lat: number; lng: number } | null
  /** T-628: the classification MapShell already resolved, forwarded so the panel and the compact
   *  card read one answer from one request. */
  siteClassification?: LocationClassification | null
}

export function InfoSidePanel({
  open,
  onClose,
  tract,
  siteScore,
  siteScoreLoading = false,
  siteScoreError,
  site = null,
  siteClassification,
}: InfoSidePanelProps) {
  // Escape closes the panel
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])

  return (
    <div
      role="complementary"
      aria-label="Tract detail panel"
      aria-hidden={!open}
      className={cn(
        'fixed top-12 right-0 bottom-0 z-20 w-[420px]',
        'bg-card border-l border-border shadow-xl',
        'flex flex-col',
        'transition-transform duration-200 ease-in-out',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      {/* ── Header — teal top stripe signals brand identity ────────────── */}
      <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-border border-t-2 border-t-primary">
        <div className="flex items-baseline gap-2 min-w-0">
          {tract && (
            <>
              {siteScoreLoading ? (
                <Spinner
                  size={16}
                  className="text-muted-foreground flex-shrink-0"
                  label="Scoring location"
                />
              ) : (
                !siteScoreError && (
                  <span
                    className="text-sm font-bold tabular-nums flex-shrink-0"
                    style={{
                      color: scoreColor(siteScore ? siteScore.opportunityScore : tract.lviScore),
                    }}
                  >
                    {Math.round(siteScore ? siteScore.opportunityScore : tract.lviScore)}
                    <span className="text-xs font-normal ml-1">{SCORE_LABELS.lvi}</span>
                  </span>
                )
              )}
            </>
          )}
        </div>

        <button
          onClick={onClose}
          aria-label="Close detail panel"
          className="ml-2 flex-shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground
                     hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-ring min-h-[36px] min-w-[36px] flex items-center justify-center"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="oy-scroll flex-1 overflow-y-auto">
        {siteScoreError && (
          <div className="mx-3 mt-3 px-2 py-1.5 bg-destructive/10 text-destructive rounded-md text-xs">
            {siteScoreError}
          </div>
        )}
        {tract && !siteScoreError && (
          <ScoreDrillDown
            tract={tract}
            siteScore={siteScore}
            site={site}
            siteClassification={siteClassification}
          />
        )}
      </div>
    </div>
  )
}
